/**
 * Security Center — editorial pillar page (Phase 4 of posture-pages-redesign).
 *
 * Replaces the legacy Tab.Group + sub-header layout with the same editorial
 * pattern shipped on the Command Center: kicker pill, italic-mixed verdict
 * headline, score box, sticky pill tabs, and per-tab editorial chrome.
 *
 * Wave-1 foundation modules (composer / hook / shared shell components) are
 * reused unchanged. The page surfaces the SAME Guardian Score composite the
 * Command Center renders — there is no separate "Security score" — so the
 * standfirst stays in lock-step across pages.
 *
 * Source-of-truth references:
 *   - .agents/Tasks/High Priority/posture-pages-redesign.md §4
 *   - .agents/Mockups/posture-pages-redesign/02-security-center.html
 *   - CLAUDE.md "Product Voice"
 *
 * Hard preserves: all five existing tab body components stay intact, the
 * framework star toggle inside <BestPracticeAnalyzerTab> is untouched,
 * severity / category / tier filters inside child tabs are untouched, the
 * Secure Score history chart and SCuBA control drilldown are untouched, and
 * tab URL sync (`?tab=…`) keeps its existing query keys.
 */

import { useEffect, useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import clsx from 'clsx'
import { StarIcon as StarOutlineIcon } from '@heroicons/react/24/outline'
import { StarIcon as StarSolidIcon } from '@heroicons/react/24/solid'

import { api } from '@/lib/api'
import {
  calculateStarredBpaScore,
  calculateUnifiedGuardianScore,
  useComplianceDashboard,
  useComplianceScoreHistory,
} from '@/hooks/useComplianceData'
import { useTenantContext } from '@/hooks/useTenantContext'
import { useSecurityBrief } from '@/hooks/useSecurityBrief'
import { usePriorityActions } from '@/hooks/usePriorityActions'
import { refMatchesFramework, pickPrimaryFrameworkRef } from '@/lib/frameworkUtils'
import type { PriorityAction, Severity } from '@/lib/posture-brief'
import type {
  ComplianceTrendPoint,
  ComplianceViolation,
  FrameworkScoreDisplay,
  SecureScoreControlProfile,
  SecureScoreSummary,
} from '@/types'

import PillarBrief from '@/components/PostureBrief/PillarBrief'
import PillarTabs, { usePillarTab } from '@/components/PostureBrief/PillarTabs'
import FooterRail from '@/components/PostureBrief/FooterRail'
import ScoreDeltaChip from '@/components/Dashboard/v2/ScoreDeltaChip'

import {
  AppPostureTab,
  BestPracticeAnalyzerTab,
  ScubaDeepScanTab,
  SecureScoreTab,
} from '@/components/SecurityCenter'
import type { OAuthAppSummary } from '@/components/SecurityCenter/appPermissionsTypes'

/* -------------------------------------------------------------------------- */
/* Tab definitions                                                            */
/* -------------------------------------------------------------------------- */

type TabKey =
  | 'overview'
  | 'best-practices'
  | 'secure-score'
  | 'scuba'
  | 'app-permissions'

// App Posture (id retained as 'app-permissions' for URL stability) sits before
// SCuBA Deep — App Posture is universally relevant; SCuBA is CISA-specific and
// deeper-cut, so it sits last. See security-center-subpages-redesign.md §13.
const ALL_TAB_IDS: ReadonlyArray<TabKey> = [
  'overview',
  'best-practices',
  'secure-score',
  'app-permissions',
  'scuba',
] as const

/* -------------------------------------------------------------------------- */
/* Page                                                                       */
/* -------------------------------------------------------------------------- */

export default function SecurityCenter() {
  const navigate = useNavigate()
  const { defaultTenantId, isLoading: isLoadingTenant } = useTenantContext()
  const [selectedTenantId, setSelectedTenantId] = useState<string | undefined>(undefined)

  // Initialize selectedTenantId from context. The sidebar TenantContextBlock
  // owns tenant switching globally — when the user changes tenants there it
  // triggers a page reload, so we only need to sync once on mount.
  useEffect(() => {
    if (!isLoadingTenant && defaultTenantId !== undefined) {
      setSelectedTenantId(defaultTenantId)
    }
  }, [isLoadingTenant, defaultTenantId])

  /* ------------------------- Tab + URL sync -------------------------------- */
  // PillarTabs handles URL sync internally. The page reads the active tab via
  // usePillarTab and renders only the matching panel — same one-source-of-truth
  // pattern the Command Center uses for Posture / Operations / History.
  const [activeTab, setActiveTab] = usePillarTab<TabKey>(
    ALL_TAB_IDS,
    'overview',
  )

  /* ------------------------- Data queries ---------------------------------- */
  // Each hook below already validates the tenant id and won't fire without
  // it — tenant isolation is honored without any additional gating here.

  const { data: dashboardStats, isLoading: statsLoading } = useQuery({
    queryKey: ['dashboard', selectedTenantId],
    queryFn: () =>
      selectedTenantId ? api.getDashboardStats(selectedTenantId) : Promise.resolve(null),
    enabled: !!selectedTenantId,
    refetchInterval: 5 * 60 * 1000,
  })

  const { data: secureScoreSummary, isLoading: secureScoreLoading } = useQuery<
    SecureScoreSummary | null
  >({
    queryKey: ['secureScoreSummary', selectedTenantId],
    queryFn: () =>
      selectedTenantId ? api.getSecureScoreSummary(selectedTenantId) : Promise.resolve(null),
    enabled: !!selectedTenantId,
    staleTime: 5 * 60 * 1000,
    refetchOnWindowFocus: false,
  })

  const { data: userInsights, isLoading: insightsLoading } = useQuery({
    queryKey: ['user-insights', selectedTenantId],
    queryFn: () => api.getUserInsights(selectedTenantId!),
    enabled: !!selectedTenantId,
    refetchInterval: 5 * 60 * 1000,
  })

  // Compliance dashboard returns frameworks, violations and the violation
  // summary in a single hook. We rely on it both for the headline composite
  // bar and for the heatmap / top-items sections below.
  const {
    dashboardData,
    violations,
    isLoading: complianceLoading,
  } = useComplianceDashboard(selectedTenantId)

  // Score history powers the heatmap's 7-day delta column.
  const { data: scoreHistory } = useComplianceScoreHistory(selectedTenantId, 14)

  // Curated BPA priority ranking — single fetch backs both the top-of-page
  // RiskCard hero (rows 01/02) AND the BpaTopItemsCard (top 4). The server-side
  // ranking already accounts for severity x weight x scope so we don't re-sort
  // on the client. See unified-ranking-score-delta-rollout.md §Phase 2.
  const { actions: priorityBpaActions, isLoading: priorityBpaLoading } = usePriorityActions({
    tenantId: selectedTenantId,
    maxCount: 4,
    pillar: 'bpa',
  })

  // OAuth apps summary drives the App Permissions tab badge AND the third
  // top-three-risks card. We only need the summary counts here; the full app
  // list is fetched lazily on the App Permissions tab.
  const { data: oauthSummary } = useQuery<OAuthAppSummary>({
    queryKey: ['oauth-apps-summary', selectedTenantId],
    queryFn: () => api.getOAuthAppSummary(selectedTenantId!),
    enabled: !!selectedTenantId,
    staleTime: 5 * 60 * 1000,
    refetchOnWindowFocus: false,
  })

  // Top tier-1 OAuth app — just the highest-criticality app. We pull a single
  // page of Critical-tier apps; this is the same shape the App Permissions
  // tab itself paginates, so React Query de-dupes when the user switches tabs.
  const { data: criticalOauthApps } = useQuery({
    queryKey: ['oauth-apps-tier1-card', selectedTenantId],
    queryFn: () =>
      api.getOAuthApps(selectedTenantId!, 1, 5, 'Critical', undefined, undefined, undefined, undefined),
    enabled: !!selectedTenantId,
    staleTime: 5 * 60 * 1000,
    refetchOnWindowFocus: false,
  })

  /* ------------------------- Score composite ------------------------------- */
  // Same starred-frameworks computation the Command Center uses. Surface the
  // SAME Guardian Score here — there is no Security-pillar-specific composite.
  const bpaStarredScore = useMemo(() => {
    const standardScores = dashboardData?.frameworks
    if (!standardScores) return undefined
    const mapped = standardScores.map(s => ({
      code: s.code,
      score: s.score,
      weight: s.weight,
      includeInGuardianScore: s.includeInGuardianScore,
    }))
    return calculateStarredBpaScore(mapped) ?? undefined
  }, [dashboardData?.frameworks])

  const guardianScore = useMemo(
    () =>
      calculateUnifiedGuardianScore({
        secureScore: secureScoreSummary?.score?.percentage ?? null,
        bpaScore: bpaStarredScore ?? null,
        mfaAdoption: userInsights?.mfaMemberAdoptionPercentage ?? null,
        configCompliance: dashboardStats?.complianceScore?.percentage ?? null,
      }),
    [
      secureScoreSummary?.score?.percentage,
      bpaStarredScore,
      userInsights?.mfaMemberAdoptionPercentage,
      dashboardStats?.complianceScore?.percentage,
    ],
  )

  // Same four-bar Guardian Score breakdown as the Command Center. The labels
  // and weights mirror Dashboard.tsx exactly so the standfirst reads identically
  // across pages.
  const compositeBars = useMemo(
    () => [
      {
        label: 'Baseline',
        weight: '20%',
        value: Math.round(dashboardStats?.complianceScore?.percentage ?? 0),
        fillClass: 'bg-primary-500',
      },
      {
        label: 'Best Practice',
        weight: '30%',
        value: Math.round(bpaStarredScore ?? 0),
        fillClass: 'bg-secondaryBlue-300',
      },
      {
        label: 'MFA',
        weight: '20%',
        value: Math.round(userInsights?.mfaMemberAdoptionPercentage ?? 0),
        fillClass: 'bg-secondarySand-300',
      },
      {
        label: 'Secure Score',
        weight: '30%',
        value: Math.round(secureScoreSummary?.score?.percentage ?? 0),
        fillClass: 'bg-primary-500',
      },
    ],
    [
      dashboardStats?.complianceScore?.percentage,
      bpaStarredScore,
      userInsights?.mfaMemberAdoptionPercentage,
      secureScoreSummary?.score?.percentage,
    ],
  )

  /* ------------------------- Editorial copy -------------------------------- */
  // Brief verdict is over Guardian Score, so only violations whose frameworks
  // actually contribute to the score are eligible to be cited as the weak link
  // or top action. Unstarred frameworks (`includeInGuardianScore=false`) are
  // intentionally excluded — citing them would tell the user "fix this to lift
  // the score" when fixing it wouldn't move the score at all.
  const starredFrameworkCodes = useMemo(
    () =>
      (dashboardData?.frameworks ?? [])
        .filter(f => f.includeInGuardianScore)
        .map(f => f.code),
    [dashboardData?.frameworks],
  )

  const scoringViolations = useMemo(() => {
    if (starredFrameworkCodes.length === 0) return []
    return violations.filter(v => {
      const refs = v.frameworkReferences ?? []
      return starredFrameworkCodes.some(code =>
        refs.some(ref => refMatchesFramework(ref, code)),
      )
    })
  }, [violations, starredFrameworkCodes])

  // Open SHALL violations restricted to score-driving (starred) frameworks.
  // Drives the brief's weak-link clause AND the Top-3 hero / Top BPA items
  // on the Overview tab.
  const openShallViolations = useMemo(
    () =>
      scoringViolations.filter(v => v.status === 'Open' && v.criticality === 'SHALL'),
    [scoringViolations],
  )

  // Top SHALL violation feeds the deck's named-risk slot. Sourced from the
  // server-curated priority ranking (same source as RiskCard #01 below) so
  // the brief and the hero card name the same fix. Picking the first item
  // out of `openShallViolations` instead would land on whatever order the
  // API returns and let the brief disagree with the queue. We still look up
  // the matching violation record by policyCode so the framework citation
  // stays precise (e.g. "MCSB PA-1", not just "MCSB").
  const topRankedAction = priorityBpaActions[0]
  const topRankedPolicyCode =
    topRankedAction?.id.startsWith('bpa:')
      ? topRankedAction.id.slice('bpa:'.length)
      : null
  const topShallViolation =
    (topRankedPolicyCode
      ? openShallViolations.find(v => v.policyCode === topRankedPolicyCode)
      : undefined)
    ?? openShallViolations[0]

  const brief = useSecurityBrief({
    tenantId: selectedTenantId,
    score: guardianScore,
    criticalCount: openShallViolations.length,
    topAction: (topRankedAction || topShallViolation)
      ? {
          title:
            topRankedAction?.title
            ?? topShallViolation?.policyTitle
            ?? topShallViolation?.policyCode
            ?? 'Untitled SHALL violation',
          framework: topShallViolation
            ? pickPrimaryFrameworkRef(
                topShallViolation.frameworkReferences,
                starredFrameworkCodes,
              ) ?? undefined
            : undefined,
          criticality: 'SHALL',
        }
      : undefined,
  })

  const briefLoading =
    statsLoading || secureScoreLoading || insightsLoading || complianceLoading

  /* ------------------------- Tab badges ------------------------------------ */
  // Each badge is derived from existing data — no new aggregator endpoints.
  const bpaShallCount = openShallViolations.length

  // Secure Score "open recommendations" — count items not yet implemented.
  // Uses the same definition the existing RecommendationsList default filter
  // uses (state: 'notImplemented').
  const secureScoreOpen = useMemo(() => {
    const recs = secureScoreSummary?.recommendations ?? []
    return recs.filter(r => !r.isImplemented).length
  }, [secureScoreSummary?.recommendations])

  // SCuBA open count: lazily fetched on the SCuBA tab itself. We surface the
  // total failures count as a reasonable proxy when summary is cached.
  const { data: scubaSummary } = useQuery({
    queryKey: ['scuba-summary', selectedTenantId],
    queryFn: () => api.getScubaSummary(selectedTenantId!),
    enabled: !!selectedTenantId,
    staleTime: 5 * 60 * 1000,
    refetchOnWindowFocus: false,
  })
  const scubaOpenCount = scubaSummary?.totalFailures ?? null

  const tier1AppCount =
    (oauthSummary?.criticalCount ?? 0) + (oauthSummary?.highCount ?? 0)

  const tabs = useMemo(
    () => [
      { id: 'overview' as TabKey, label: 'Overview', badge: null },
      {
        id: 'best-practices' as TabKey,
        label: 'Best Practice',
        badge: bpaShallCount > 0 ? bpaShallCount : null,
      },
      {
        id: 'secure-score' as TabKey,
        label: 'Secure Score',
        badge: secureScoreOpen > 0 ? secureScoreOpen : null,
      },
      {
        id: 'app-permissions' as TabKey,
        label: 'App Posture',
        badge: tier1AppCount > 0 ? tier1AppCount : null,
      },
      {
        id: 'scuba' as TabKey,
        label: 'SCuBA Deep',
        badge: scubaOpenCount && scubaOpenCount > 0 ? scubaOpenCount : null,
      },
    ],
    [bpaShallCount, secureScoreOpen, scubaOpenCount, tier1AppCount],
  )

  /* ------------------------- Render ---------------------------------------- */
  return (
    <div className="mx-auto max-w-[1320px]">
      {/* Only the Overview tab uses the parent Guardian-Score-level header.
          The other four tabs (best-practices, secure-score, app-permissions,
          scuba) each render their own pillar-specific PillarBrief — stacking
          the parent on top would double-up the header. SCuBA in particular
          doesn't roll into Guardian Score, so a Guardian-composite header
          would mis-frame the page. */}
      {activeTab === 'overview' && (
        <PillarBrief
          brief={brief}
          score={guardianScore}
          components={compositeBars}
          tone="bpa"
          isLoading={briefLoading}
        />
      )}

      <PillarTabs<TabKey>
        tabs={tabs}
        defaultTab="overview"
        tone="bpa"
        ariaLabel="Security view"
        actions={
          <button
            type="button"
            onClick={() => navigate('/dashboard')}
            className="
              inline-flex items-center gap-2 rounded-full border border-dark-600
              bg-dark-800 px-4 py-2 text-[11px] font-bold uppercase tracking-[0.16em]
              text-slate-200 transition-colors hover:border-secondaryBlue-300
              dark:hover:border-secondaryBlue-300
            "
          >
            Back to Command Center
          </button>
        }
      />

      {/* All tab panels share the BPA tone — set once on the wrapper so the
          editorial chrome (italic emphasis, severity bars, sparklines) reads
          the right CSS variables without us re-stating per-component. */}
      <div data-tone="bpa">
        {activeTab === 'overview' && (
          <div id="panel-overview" role="tabpanel" aria-labelledby="tab-overview">
            <OverviewTab
              violations={violations}
              frameworks={dashboardData?.frameworks ?? []}
              scoreHistory={scoreHistory ?? []}
              secureScoreSummary={secureScoreSummary ?? null}
              criticalOauthApp={criticalOauthApps?.items?.[0]}
              isLoading={complianceLoading}
              priorityBpaActions={priorityBpaActions}
              priorityBpaLoading={priorityBpaLoading}
              onSwitchTab={setActiveTab}
            />
          </div>
        )}

        {activeTab === 'best-practices' && (
          <div id="panel-best-practices" role="tabpanel" aria-labelledby="tab-best-practices">
            <BestPracticeAnalyzerTab tenantId={selectedTenantId} />
          </div>
        )}

        {activeTab === 'secure-score' && (
          <div id="panel-secure-score" role="tabpanel" aria-labelledby="tab-secure-score">
            {selectedTenantId ? (
              <SecureScoreTab tenantId={selectedTenantId} />
            ) : (
              <div className="rounded-[4px] border border-[color:var(--color-rule,#E6E1D5)] bg-white px-6 py-12 text-center text-sm italic text-[color:var(--color-mute,#6B7280)]">
                Please select a tenant to view Secure Score data.
              </div>
            )}
          </div>
        )}

        {activeTab === 'scuba' && (
          <div id="panel-scuba" role="tabpanel" aria-labelledby="tab-scuba">
            <ScubaDeepScanTab tenantId={selectedTenantId} />
          </div>
        )}

        {activeTab === 'app-permissions' && (
          <div id="panel-app-permissions" role="tabpanel" aria-labelledby="tab-app-permissions">
            <AppPostureTab tenantId={selectedTenantId} />
          </div>
        )}
      </div>

      <FooterRail currentTone="bpa" />
    </div>
  )
}

/* -------------------------------------------------------------------------- */
/* Overview tab — top-three risks hero + framework heatmap + two-up           */
/* -------------------------------------------------------------------------- */

interface OverviewTabProps {
  /** All open + closed violations — used by the framework heatmap so that
   *  unstarred frameworks still show their per-framework counts (the heatmap
   *  is intentionally cross-framework and uses the star icon to flag which
   *  rows actually drive Guardian Score). */
  violations: ComplianceViolation[]
  frameworks: FrameworkScoreDisplay[]
  scoreHistory: ComplianceTrendPoint[]
  secureScoreSummary: SecureScoreSummary | null
  criticalOauthApp:
    | {
        id: string
        displayName: string
        publisherName: string | null
        isVerifiedPublisher: boolean
        criticalPermissionCount: number
        highPermissionCount: number
      }
    | undefined
  isLoading: boolean
  /** Curated BPA priority actions. Backs the RiskCard hero (rows 01/02) and
   *  the BpaTopItemsCard. Comes from `usePriorityActions({ pillar: 'bpa' })`
   *  in the page component so a single fetch feeds both surfaces. */
  priorityBpaActions: PriorityAction[]
  priorityBpaLoading: boolean
  onSwitchTab: (next: TabKey) => void
}

function OverviewTab({
  violations,
  frameworks,
  scoreHistory,
  secureScoreSummary,
  criticalOauthApp,
  isLoading,
  priorityBpaActions,
  priorityBpaLoading,
  onSwitchTab,
}: OverviewTabProps) {
  /* -------- Top three risks --------------------------------------------- */
  // Pulled from the curated server-side ranking. The ranking already weighs
  // severity x policy_weight x frameworks x log(scope+1) and respects the
  // starred-framework filter, so the first two rows are "what to fix first
  // to lift the score" by construction.
  const top1 = priorityBpaActions[0]
  const top2 = priorityBpaActions[1]

  /* -------- Framework heatmap rows -------------------------------------- */
  // Mirror the BPA tab / PillarBpa rule: only score-driving frameworks
  // appear. Unstarred frameworks would dilute the heatmap with rows the user
  // has explicitly excluded from Guardian Score. MCSB is always included.
  const heatmapRows = useMemo(() => {
    const visible = frameworks.filter(
      fw => fw.includeInGuardianScore || (fw.code ?? '').toUpperCase() === 'MCSB',
    )
    return visible.map(fw => {
      // refMatchesFramework already handles SCuBA/CIS/NIST/etc pattern aliasing.
      const matches = (v: ComplianceViolation) =>
        (v.frameworkReferences ?? []).some(ref => refMatchesFramework(ref, fw.code))

      const open = violations.filter(v => v.status === 'Open' && matches(v))
      const shall  = open.filter(v => v.criticality === 'SHALL').length
      const should = open.filter(v => v.criticality === 'SHOULD').length
      const may    = open.filter(v => v.criticality === 'MAY').length

      // 7-day delta — diff today's framework score against the closest point
      // ~7 days ago. ComplianceTrendPoint groups by date and exposes per-
      // framework values via `frameworkScores: { code, score }[]`.
      const delta7d = compute7dFrameworkDelta(scoreHistory, fw.code, fw.score)

      return {
        code: fw.code,
        name: fw.name,
        controlCount: fw.totalControls,
        shall,
        should,
        may,
        dataUnavailable: fw.dataUnavailableControls,
        score: fw.score,
        delta7d,
        starred: fw.includeInGuardianScore,
      }
    })
  }, [frameworks, violations, scoreHistory])

  /* -------- Top BPA items + Top Secure Score recs ----------------------- */
  // Same source as top1/top2 above — both surfaces share the curated ranking
  // and the same fetch (capped at 4 in the page-level hook).
  const topBpaItems = priorityBpaActions.slice(0, 4)

  const topSecureScoreRecs = useMemo(() => {
    const recs = secureScoreSummary?.recommendations ?? []
    // Highest pt-impact open recommendations first. Pt impact for Secure Score
    // is real: maxScore - achievedScore for each control profile.
    return [...recs]
      .filter(r => !r.isImplemented)
      .map(r => ({ rec: r, gap: Math.max(0, (r.maxScore ?? 0) - (r.achievedScore ?? 0)) }))
      .sort((a, b) => b.gap - a.gap)
      .slice(0, 4)
  }, [secureScoreSummary?.recommendations])

  /* -------- Render ------------------------------------------------------- */
  return (
    <div className="space-y-9">
      {/* SECTION 01 — TOP THREE RISKS */}
      <section aria-labelledby="risks-headline">
        <SectionEyebrow text="Section · 01" />
        <SectionHeadline>
          The three things <em>worth fixing first</em>
        </SectionHeadline>

        <div
          className="
            grid grid-cols-1 overflow-hidden rounded-3xl border border-dark-600
            bg-dark-800 shadow-[0_24px_60px_-10px_rgba(33,41,56,0.12)]
            md:grid-cols-3
          "
        >
          <RiskCard
            rank="01"
            action={top1}
            ctaLabel="Open analyzer"
            onCta={() => onSwitchTab('best-practices')}
            isLoading={isLoading || priorityBpaLoading}
          />
          <RiskCard
            rank="02"
            action={top2}
            ctaLabel="Open analyzer"
            onCta={() => onSwitchTab('best-practices')}
            isLoading={isLoading || priorityBpaLoading}
          />
          <AppRiskCard
            rank="03"
            app={criticalOauthApp}
            onCta={() => onSwitchTab('app-permissions')}
          />
        </div>
      </section>

      {/* SECTION 02 — FRAMEWORK HEATMAP */}
      <section aria-labelledby="heatmap-headline">
        <div
          className="
            rounded-3xl border border-dark-600 bg-dark-800 px-7 py-7
            shadow-[0_24px_60px_-10px_rgba(33,41,56,0.12)]
          "
        >
          <header className="mb-5 flex flex-wrap items-end justify-between gap-3.5">
            <div>
              <SectionEyebrow text="Section · 02" />
              <SectionHeadline>
                Framework <em>heatmap</em>
              </SectionHeadline>
            </div>
            <HeatmapLegend />
          </header>

          {heatmapRows.length === 0 ? (
            <p className="py-8 text-center text-[13px] text-slate-400">
              No starred frameworks. Star one in{' '}
              <button
                type="button"
                onClick={() => onSwitchTab('best-practices')}
                className="font-semibold text-secondaryBlue-300 underline-offset-4 hover:underline"
              >
                Best Practice
              </button>{' '}
              to include it here.
            </p>
          ) : (
            <Heatmap rows={heatmapRows} />
          )}
        </div>
      </section>

      {/* SECTION 03 + 04 — TWO-UP */}
      <section className="grid grid-cols-1 gap-7 md:grid-cols-2">
        <BpaTopItemsCard
          items={topBpaItems}
          isLoading={priorityBpaLoading}
          onOpen={() => onSwitchTab('best-practices')}
        />
        <SecureScoreTopRecsCard
          recs={topSecureScoreRecs}
          summary={secureScoreSummary}
          onOpen={() => onSwitchTab('secure-score')}
        />
      </section>
    </div>
  )
}

/* -------------------------------------------------------------------------- */
/* Helpers                                                                    */
/* -------------------------------------------------------------------------- */

/** Compute the 7-day score delta for one framework from the trend-point series.
 *  Falls back to `null` when the history is too short to compare honestly —
 *  matching the pattern PillarBpa uses for its 7d trend. */
function compute7dFrameworkDelta(
  history: ComplianceTrendPoint[],
  frameworkCode: string,
  currentScore: number,
): number | null {
  if (!history || history.length === 0) return null

  // We want the score from a point ~7 days ago (or the oldest point if the
  // tenant doesn't have that much history yet).
  const sevenDaysAgo = Date.now() - 7 * 24 * 60 * 60 * 1000
  const sorted = [...history].sort(
    (a, b) => new Date(a.date).getTime() - new Date(b.date).getTime(),
  )

  let baseline: number | null = null
  for (const point of sorted) {
    if (new Date(point.date).getTime() <= sevenDaysAgo) {
      const fw = point.frameworkScores.find(f => f.code === frameworkCode)
      if (fw) baseline = fw.score
    } else {
      break
    }
  }

  // Fall back to the oldest point we DO have, so a brand-new tenant still
  // sees something instead of a silent dash.
  if (baseline === null) {
    for (const point of sorted) {
      const fw = point.frameworkScores.find(f => f.code === frameworkCode)
      if (fw) {
        baseline = fw.score
        break
      }
    }
  }

  if (baseline === null) return null
  return Math.round(currentScore - baseline)
}

/** Map an open-violation count to one of the heatmap intensity levels.
 *  l0 = healthy (zero violations of this criticality), l1-l4 ramp up by count.
 *  l4.crit reserved for SHALL columns with 5+ items, matching the mockup. */
function intensityClass(count: number, isShall: boolean): string {
  if (count === 0) return 'h-cell-l0'
  if (isShall) {
    if (count >= 5) return 'h-cell-l4-crit'
    if (count >= 3) return 'h-cell-l4'
    return 'h-cell-l3'
  }
  if (count >= 7) return 'h-cell-l3'
  if (count >= 3) return 'h-cell-l2'
  return 'h-cell-l1'
}

/* -------------------------------------------------------------------------- */
/* Top-three-risks card sub-components                                        */
/* -------------------------------------------------------------------------- */

function RiskCard({
  rank,
  action,
  ctaLabel,
  onCta,
  isLoading,
}: {
  rank: string
  action: PriorityAction | undefined
  ctaLabel: string
  onCta: () => void
  isLoading: boolean
}) {
  if (!action) {
    return (
      <div
        className={clsx(
          'border-r border-dark-700 px-7 py-8 last:border-r-0',
          'border-t-[4px]',
        )}
        style={{ borderTopColor: '#6B7280' }}
      >
        <div className="font-display text-[56px] font-extrabold italic leading-[0.85] text-slate-700">
          {rank}
        </div>
        <p className="mt-3 text-[13px] text-slate-400">
          {isLoading ? 'Loading risks…' : 'No open priorities in this slot.'}
        </p>
      </div>
    )
  }

  // Map PriorityAction.severity (5 levels) onto our 3-tone palette. The BPA
  // mapper only emits crit/high/low for SHALL/SHOULD/MAY today, but keep the
  // mapping defensive so future severity buckets don't blow up the UI.
  const tone = severityToToneKey(action.severity)
  const sevColors = SEV_TONE[tone]
  const sevLabel = SEV_LABEL[tone]
  const criticality = severityToCriticalityLabel(action.severity)
  const affected = action.scope ?? null

  return (
    <div
      className={clsx(
        'flex flex-col border-r border-dark-700 px-7 py-8 last:border-r-0',
        'border-t-[4px]',
      )}
      style={{ borderTopColor: sevColors.bar }}
    >
      <div
        className="font-display text-[56px] font-extrabold italic leading-[0.85]"
        style={{ color: sevColors.bar, letterSpacing: '-0.04em' }}
      >
        {rank}
      </div>

      <span
        className="
          mt-3.5 inline-flex items-center gap-1.5 self-start rounded-full border
          px-2.5 py-1 text-[10px] font-bold uppercase tracking-[0.16em]
        "
        style={{
          color: sevColors.bar,
          borderColor: sevColors.border,
          background: sevColors.background,
        }}
      >
        {sevLabel} · {criticality}
      </span>

      <div className="mt-3 flex flex-wrap items-center gap-2">
        <h3
          className="font-display text-[18px] font-extrabold leading-[1.25] tracking-[-0.012em]"
          style={{ color: 'rgb(var(--color-text-primary))' }}
        >
          {action.title}
        </h3>
        <ScoreDeltaChip delta={action.guardianScoreDelta} />
      </div>

      <p className="mt-2.5 text-[13px] leading-[1.55] text-slate-300 dark:text-slate-300">
        {action.meta}
      </p>

      <footer className="mt-auto flex items-center justify-between gap-3 border-t border-dark-700 pt-4 text-[11px] font-bold uppercase tracking-[0.14em]">
        <span className="text-slate-500">
          {affected != null
            ? `${affected} ${affected === 1 ? 'resource' : 'resources'} affected`
            : 'Best Practice'}
        </span>
        <button
          type="button"
          onClick={onCta}
          className="border-b-2 border-secondaryBlue-300 pb-0.5 text-slate-100 transition-colors hover:text-secondaryBlue-300"
        >
          {ctaLabel} →
        </button>
      </footer>
    </div>
  )
}

function AppRiskCard({
  rank,
  app,
  onCta,
}: {
  rank: string
  app: OverviewTabProps['criticalOauthApp']
  onCta: () => void
}) {
  if (!app) {
    return (
      <div
        className={clsx(
          'border-r border-dark-700 px-7 py-8 last:border-r-0',
          'border-t-[4px]',
        )}
        style={{ borderTopColor: '#6B7280' }}
      >
        <div className="font-display text-[56px] font-extrabold italic leading-[0.85] text-slate-700">
          {rank}
        </div>
        <p className="mt-3 text-[13px] text-slate-400">
          No tier-1 app permission risks today.
        </p>
      </div>
    )
  }

  const sev = SEV_TONE.med
  const tierCount = (app.criticalPermissionCount ?? 0) + (app.highPermissionCount ?? 0)
  const publisher = app.publisherName || 'unverified publisher'

  return (
    <div
      className={clsx(
        'flex flex-col border-r border-dark-700 px-7 py-8 last:border-r-0',
        'border-t-[4px]',
      )}
      style={{ borderTopColor: sev.bar }}
    >
      <div
        className="font-display text-[56px] font-extrabold italic leading-[0.85]"
        style={{ color: sev.bar, letterSpacing: '-0.04em' }}
      >
        {rank}
      </div>

      <span
        className="
          mt-3.5 inline-flex items-center gap-1.5 self-start rounded-full border
          px-2.5 py-1 text-[10px] font-bold uppercase tracking-[0.16em]
        "
        style={{
          color: sev.bar,
          borderColor: sev.border,
          background: sev.background,
        }}
      >
        Tier-1 · App
      </span>

      <h3
        className="mt-3 font-display text-[18px] font-extrabold leading-[1.25] tracking-[-0.012em]"
        style={{ color: 'rgb(var(--color-text-primary))' }}
      >
        {app.displayName || 'Unverified application'}
      </h3>

      <p className="mt-2.5 text-[13px] leading-[1.55] text-slate-300 dark:text-slate-300">
        {app.isVerifiedPublisher
          ? `Verified publisher (${publisher}) holds high-privilege scopes. Review and revoke if not part of an active integration.`
          : `Unverified publisher (${publisher}) holds high-privilege scopes. Review and revoke if not part of an active integration.`}
      </p>

      <footer className="mt-auto flex items-center justify-between gap-3 border-t border-dark-700 pt-4 text-[11px] font-bold uppercase tracking-[0.14em]">
        <span className="text-slate-500">
          {tierCount > 0
            ? `${tierCount} ${tierCount === 1 ? 'permission' : 'permissions'} affected · `
            : ''}
          App Permissions · Tier-1
        </span>
        <button
          type="button"
          onClick={onCta}
          className="border-b-2 border-secondaryBlue-300 pb-0.5 text-slate-100 transition-colors hover:text-secondaryBlue-300"
        >
          Open App Permissions →
        </button>
      </footer>
    </div>
  )
}

/* -------------------------------------------------------------------------- */
/* Heatmap                                                                    */
/* -------------------------------------------------------------------------- */

interface HeatmapRow {
  code: string
  name: string
  controlCount: number
  shall: number
  should: number
  may: number
  dataUnavailable: number
  score: number
  delta7d: number | null
  starred: boolean
}

function Heatmap({ rows }: { rows: HeatmapRow[] }) {
  return (
    <div
      className="
        grid overflow-hidden rounded-2xl border border-dark-700
      "
      style={{ gridTemplateColumns: '220px repeat(4, minmax(0,1fr)) 110px' }}
    >
      {/* Header */}
      <HeatmapHead className="text-left">Framework</HeatmapHead>
      <HeatmapHead>SHALL</HeatmapHead>
      <HeatmapHead>SHOULD</HeatmapHead>
      <HeatmapHead>MAY</HeatmapHead>
      <HeatmapHead>N/A</HeatmapHead>
      <HeatmapHead className="border-r-0">Score</HeatmapHead>

      {rows.map(row => (
        <HeatmapRowCells key={row.code} row={row} />
      ))}
    </div>
  )
}

function HeatmapHead({
  children,
  className,
}: {
  children: React.ReactNode
  className?: string
}) {
  return (
    <div
      className={clsx(
        'border-b border-r border-dark-700 bg-dark-900 px-4 py-3.5 text-center text-[10px] font-bold uppercase tracking-[0.16em] text-slate-500',
        className,
      )}
    >
      {children}
    </div>
  )
}

function HeatmapRowCells({ row }: { row: HeatmapRow }) {
  return (
    <>
      {/* Row label — pillar name + control count + star indicator. */}
      <div className="flex flex-col justify-center gap-0.5 border-b border-r border-dark-700 bg-dark-800 px-4 py-3.5">
        <div className="flex items-center gap-2">
          {row.starred ? (
            <StarSolidIcon
              className="h-3.5 w-3.5 flex-shrink-0"
              style={{ color: 'rgb(var(--color-secondary-blue-300))' }}
              title="Contributes to Guardian Score"
              aria-label="Starred — contributes to Guardian Score"
            />
          ) : (
            <StarOutlineIcon
              className="h-3.5 w-3.5 flex-shrink-0 text-slate-600"
              title="Not in Guardian Score"
              aria-label="Not in Guardian Score"
            />
          )}
          <span
            className="text-[13px] font-bold"
            style={{ color: 'rgb(var(--color-text-primary))' }}
          >
            {shortFrameworkLabel(row.code, row.name)}
          </span>
        </div>
        <span className="ml-5 text-[10px] font-medium uppercase tracking-[0.12em] text-slate-500">
          {row.controlCount} control{row.controlCount === 1 ? '' : 's'}
        </span>
      </div>

      <HeatmapCell intensity={intensityClass(row.shall, true)}>{row.shall}</HeatmapCell>
      <HeatmapCell intensity={intensityClass(row.should, false)}>{row.should}</HeatmapCell>
      <HeatmapCell intensity={intensityClass(row.may, false)}>{row.may}</HeatmapCell>
      <HeatmapCell intensity="h-cell-l0">{row.dataUnavailable}</HeatmapCell>

      <div className="flex flex-col items-center justify-center border-b border-dark-700 bg-dark-900 px-3 py-3.5 tabular-nums">
        <span
          className="text-[16px] font-extrabold"
          style={{ color: 'rgb(var(--color-text-primary))' }}
        >
          {Math.round(row.score)}%
        </span>
        <span className="mt-0.5 text-[10px] font-semibold uppercase tracking-[0.14em] text-slate-500">
          {formatDelta(row.delta7d)}
        </span>
      </div>
    </>
  )
}

function HeatmapCell({
  intensity,
  children,
}: {
  intensity: string
  children: React.ReactNode
}) {
  // Map intensity slug to Tailwind utility classes. We pin the colors here so
  // the heatmap reads consistently in both light and dark mode — they're
  // editorial accents, not surface tokens, and intentionally don't theme-swap.
  const styles = HEATMAP_INTENSITY[intensity] ?? HEATMAP_INTENSITY['h-cell-l0']
  return (
    <div
      className="flex items-center justify-center border-b border-r border-dark-700 px-4 py-3.5 text-[13px] font-bold tabular-nums"
      style={styles}
    >
      {children}
    </div>
  )
}

const HEATMAP_INTENSITY: Record<string, React.CSSProperties> = {
  'h-cell-l0':       { background: 'rgba(118,214,157,0.18)', color: '#4A9B6E' },
  'h-cell-l1':       { background: 'rgba(167,211,240,0.16)', color: '#4F86A8' },
  'h-cell-l2':       { background: 'rgba(167,211,240,0.34)', color: '#4F86A8' },
  'h-cell-l3':       { background: 'rgba(235,212,180,0.5)',  color: '#C9AD80' },
  'h-cell-l4':       { background: 'rgba(229,115,115,0.32)', color: '#C84B4B' },
  'h-cell-l4-crit':  { background: 'rgba(200,75,75,0.85)',   color: 'white' },
}

function HeatmapLegend() {
  return (
    <div className="flex flex-wrap items-center gap-3 text-[10px] font-semibold uppercase tracking-[0.16em] text-slate-500">
      <LegendSwatch label="Healthy" style={{ background: 'rgba(118,214,157,0.18)', borderColor: 'rgba(74,155,110,0.4)' }} />
      <LegendSwatch label="Watch"   style={{ background: 'rgba(167,211,240,0.34)', borderColor: 'rgba(79,134,168,0.4)' }} />
      <LegendSwatch label="Concern" style={{ background: 'rgba(235,212,180,0.5)',  borderColor: 'rgba(201,173,128,0.4)' }} />
      <LegendSwatch label="At-risk" style={{ background: 'rgba(229,115,115,0.32)', borderColor: 'rgba(200,75,75,0.4)' }} />
      <LegendSwatch label="Critical" style={{ background: 'rgba(200,75,75,0.85)', borderColor: 'rgba(200,75,75,0.85)' }} />
    </div>
  )
}

function LegendSwatch({ label, style }: { label: string; style: React.CSSProperties }) {
  return (
    <span className="inline-flex items-center gap-1.5">
      <span
        aria-hidden
        className="inline-block h-3.5 w-3.5 rounded-[3px] border"
        style={style}
      />
      {label}
    </span>
  )
}

function formatDelta(delta: number | null): string {
  if (delta == null) return '— 7d'
  if (delta === 0) return 'flat 7d'
  const sign = delta > 0 ? '+' : '−'
  return `${sign}${Math.abs(delta)} pt${Math.abs(delta) === 1 ? '' : 's'} 7d`
}

/* -------------------------------------------------------------------------- */
/* BPA top items + Secure Score top recs                                      */
/* -------------------------------------------------------------------------- */

function BpaTopItemsCard({
  items,
  isLoading,
  onOpen,
}: {
  items: PriorityAction[]
  isLoading: boolean
  onOpen: () => void
}) {
  return (
    <div className="rounded-3xl border border-dark-600 bg-dark-800 px-7 py-7 shadow-[0_24px_60px_-10px_rgba(33,41,56,0.12)]">
      <header className="mb-4 flex items-end justify-between gap-3 border-b border-dark-700 pb-3.5">
        <div>
          <SectionEyebrow text="Section · 03" />
          <SectionHeadline level="card">
            Best Practice <em>top items</em>
          </SectionHeadline>
        </div>
        <button
          type="button"
          onClick={onOpen}
          className="text-[11px] font-bold uppercase tracking-[0.14em] text-slate-400 transition-colors hover:text-secondaryBlue-300"
        >
          Open analyzer →
        </button>
      </header>

      {isLoading && items.length === 0 ? (
        <p className="py-6 text-[13px] text-slate-400">Loading top items…</p>
      ) : items.length === 0 ? (
        <p className="py-6 text-[13px] text-slate-400">
          No top items today. Posture is settled here.
        </p>
      ) : (
        <ul role="list" className="divide-y divide-dark-700">
          {items.map(a => {
            const tone = SEV_TONE[severityToToneKey(a.severity)]
            const affected = a.scope ?? null
            return (
              <li
                key={a.id}
                className="grid gap-3.5 py-3.5"
                style={{ gridTemplateColumns: '4px 1fr 70px' }}
              >
                <span
                  aria-hidden
                  className="self-stretch rounded-full"
                  style={{ background: tone.bar }}
                />
                <div className="min-w-0">
                  <div className="flex flex-wrap items-center gap-2">
                    <div
                      className="text-[14px] font-bold leading-tight"
                      style={{ color: 'rgb(var(--color-text-primary))' }}
                    >
                      {a.title}
                    </div>
                    <ScoreDeltaChip delta={a.guardianScoreDelta} />
                  </div>
                  <div className="mt-1 text-[10px] font-semibold uppercase tracking-[0.14em] text-slate-500">
                    {a.meta}
                  </div>
                </div>
                <div className="text-right tabular-nums">
                  <div
                    className="font-display text-[18px] font-extrabold italic leading-none"
                    style={{ color: tone.bar, letterSpacing: '-0.02em' }}
                  >
                    {affected ?? '—'}
                  </div>
                  <div className="mt-1 text-[9px] font-semibold uppercase tracking-[0.14em] text-slate-500">
                    resources affected
                  </div>
                </div>
              </li>
            )
          })}
        </ul>
      )}
    </div>
  )
}

function SecureScoreTopRecsCard({
  recs,
  summary,
  onOpen,
}: {
  recs: { rec: SecureScoreControlProfile; gap: number }[]
  summary: SecureScoreSummary | null
  onOpen: () => void
}) {
  const score = summary?.score?.percentage
  return (
    <div className="rounded-3xl border border-dark-600 bg-dark-800 px-7 py-7 shadow-[0_24px_60px_-10px_rgba(33,41,56,0.12)]">
      <header className="mb-4 flex items-end justify-between gap-3 border-b border-dark-700 pb-3.5">
        <div>
          <SectionEyebrow text="Section · 04" />
          <SectionHeadline level="card">
            Secure Score <em>top recs</em>
          </SectionHeadline>
        </div>
        {score != null && (
          <div
            className="font-display text-[32px] font-extrabold leading-none tabular-nums"
            style={{ color: 'var(--tone-accent-deep)', letterSpacing: '-0.04em' }}
          >
            {Math.round(score)}
            <span className="ml-1 text-[14px] font-semibold text-slate-500">/100</span>
          </div>
        )}
      </header>

      {recs.length === 0 ? (
        <p className="py-6 text-[13px] text-slate-400">
          No open Secure Score recommendations to surface.
        </p>
      ) : (
        <ul role="list" className="divide-y divide-dark-700">
          {recs.map(({ rec, gap }) => {
            const sev = severityFromSecureScoreRank(rec.rank, gap)
            const tone = SEV_TONE[sev]
            return (
              <li
                key={rec.id}
                className="grid gap-3.5 py-3.5"
                style={{ gridTemplateColumns: '4px 1fr 70px' }}
              >
                <span
                  aria-hidden
                  className="self-stretch rounded-full"
                  style={{ background: tone.bar }}
                />
                <div className="min-w-0">
                  <div
                    className="text-[14px] font-bold leading-tight"
                    style={{ color: 'rgb(var(--color-text-primary))' }}
                  >
                    {rec.title}
                  </div>
                  <div className="mt-1 text-[10px] font-semibold uppercase tracking-[0.14em] text-slate-500">
                    {rec.controlCategory} · {rec.service}
                  </div>
                </div>
                <div className="text-right tabular-nums">
                  <div
                    className="font-display text-[18px] font-extrabold italic leading-none"
                    style={{ color: tone.bar, letterSpacing: '-0.02em' }}
                  >
                    +{gap.toFixed(0)}
                  </div>
                  <div className="mt-1 text-[9px] font-semibold uppercase tracking-[0.14em] text-slate-500">
                    pts est.
                  </div>
                </div>
              </li>
            )
          })}
        </ul>
      )}

      <footer className="mt-4 flex justify-end border-t border-dark-700 pt-3.5">
        <button
          type="button"
          onClick={onOpen}
          className="text-[11px] font-bold uppercase tracking-[0.14em] text-slate-400 transition-colors hover:text-secondaryBlue-300"
        >
          All recommendations →
        </button>
      </footer>
    </div>
  )
}

/* -------------------------------------------------------------------------- */
/* Editorial micro-components                                                 */
/* -------------------------------------------------------------------------- */

function SectionEyebrow({ text }: { text: string }) {
  return (
    <div className="text-[10px] font-bold uppercase tracking-[0.22em] text-slate-500">
      {text}
    </div>
  )
}

function SectionHeadline({
  children,
  level = 'page',
}: {
  children: React.ReactNode
  level?: 'page' | 'card'
}) {
  // The headline color resolves against `rgb(var(--color-text-primary))` so it
  // tracks the active theme. Emphasis inside is colored per-tone by the inline
  // `[&_em]` Tailwind utility — pulls `--tone-accent-deep` from the nearest
  // [data-tone] ancestor, which on this page is the OverviewTab wrapper.
  const sizeClass = level === 'card' ? 'text-[20px]' : 'text-[28px]'
  return (
    <h2
      className={clsx(
        'mt-1 font-display font-extrabold tracking-[-0.018em]',
        sizeClass,
        '[&_em]:italic [&_em]:font-semibold [&_em]:[color:var(--tone-accent-deep)]',
      )}
      style={{ color: 'rgb(var(--color-text-primary))' }}
    >
      {children}
    </h2>
  )
}

/* -------------------------------------------------------------------------- */
/* Tone + helpers                                                             */
/* -------------------------------------------------------------------------- */

const SEV_TONE = {
  crit: {
    bar: '#C84B4B',
    border: 'rgba(229,115,115,0.4)',
    background: 'rgba(229,115,115,0.08)',
  },
  high: {
    bar: '#C9AD80',
    border: 'rgba(201,173,128,0.4)',
    background: 'rgba(235,212,180,0.18)',
  },
  med: {
    bar: '#4F86A8',
    border: 'rgba(79,134,168,0.4)',
    background: 'rgba(167,211,240,0.18)',
  },
} as const

const SEV_LABEL: Record<keyof typeof SEV_TONE, string> = {
  crit: 'Critical',
  high: 'High',
  med:  'Medium',
}

/** Map a curated PriorityAction.severity (5 levels) onto the 3-tone SEV_TONE
 *  palette this page uses for its bars and pills. Defensive: the BPA mapper
 *  emits crit/high/low for SHALL/SHOULD/MAY today, but the Severity union also
 *  carries 'med' and 'routine' which map cleanly onto our existing tones. */
function severityToToneKey(severity: Severity): keyof typeof SEV_TONE {
  switch (severity) {
    case 'crit': return 'crit'
    case 'high': return 'high'
    case 'med':  return 'med'
    // 'low' and 'routine' both fall back to the medium tone — they're the same
    // visual band on this page and the action ranking already orders them
    // below crit/high so they only appear when nothing more urgent is open.
    case 'low':
    case 'routine':
    default:     return 'med'
  }
}

/** Map severity → the SHALL/SHOULD/MAY criticality label shown on the RiskCard
 *  pill. The BPA-pillar mapper preserves this 1:1 today (SHALL→crit etc), so
 *  the inverse is meaningful for end-user copy. Low/routine collapse to MAY. */
function severityToCriticalityLabel(severity: Severity): 'SHALL' | 'SHOULD' | 'MAY' {
  if (severity === 'crit') return 'SHALL'
  if (severity === 'high') return 'SHOULD'
  return 'MAY'
}

/** Derive a severity bar color for Secure Score recs. Microsoft doesn't ship
 *  a SHALL/SHOULD/MAY equivalent, so we fall back to the absolute pt gap as
 *  the strongest available signal. Same buckets as the BPA severity scale. */
function severityFromSecureScoreRank(
  rank: number | undefined,
  gap: number,
): keyof typeof SEV_TONE {
  if (gap >= 5) return 'crit'
  if (gap >= 3) return 'high'
  // Rank 1-3 are Microsoft-flagged "highest impact" — promote them even if
  // the pt gap is small (e.g. partially-implemented).
  if (rank != null && rank <= 3) return 'high'
  return 'med'
}

function shortFrameworkLabel(code: string, name: string): string {
  // Keep the heatmap rows visually consistent without bulldozing the
  // backend display name. Long codes (NIST_800_53 etc) get a short form;
  // everything else falls through to the friendly name.
  const upper = code.toUpperCase()
  if (upper.startsWith('NIST_800_53')) return 'NIST 800-53'
  if (upper.startsWith('NIST_CSF')) return 'NIST CSF'
  if (upper === 'SCUBA') return 'SCuBA'
  if (upper === 'MCSB') return 'MCSB'
  if (upper === 'CIS') return 'CIS Microsoft 365'
  if (upper === 'EIDSCA') return 'EIDSCA'
  return name || code
}
